import type { PromotionTypesValues } from '../../common/promotion_type.enum';
import { API_METHODS } from './api.enums';
import { apiAction } from './utils.actions';

export type GetEmployerCreditsResponse = {
    totalPromotions: number, totalAllowedJobs: number, totalCredits: number, tilesMode: boolean,
}

export type AdminEmployerAccountResponse = Pick<
GetEmployerCreditsResponse,
'totalPromotions' | 'totalAllowedJobs' | 'tilesMode'
> & {
    totalExternalJobs: number
    employerEmail: string | null
}

export const getEmployerCredits = (
    backdrop = false,
) => apiAction<GetEmployerCreditsResponse>({
    url: '/api/employer/jobs-credits',
    method: API_METHODS.GET,
    backdrop,
});

export const getEmployerExternalJobsCount = (
    backdrop = false,
) => apiAction<{ totalExternalJobs: number }>({
    url: '/api/employer/external-jobs/count',
    method: API_METHODS.GET,
    backdrop,
});

export const getAdminEmployerAccount = (
    userid: number, backdrop = false,
) => apiAction<AdminEmployerAccountResponse>({
    url: `/api/admin/users/${userid}/employer-account`,
    method: API_METHODS.GET,
    backdrop,
});

export const bumpJob = (
    params: { jobid: number }, backdrop = true,
) => apiAction<void>({
    url: `/api/jobs/${params.jobid}/extend`,
    method: API_METHODS.POST,
    backdrop,
});

export type AddPromotionPayload = {
    userid: number
    type?: PromotionTypesValues
    amount: number
    subscription?: 'monthly' | 'annual'
}

export type EmployerSubscription = {
    type: 'jobs_tiles'
    description: string
    startDate: string
    endDate: string | null
    isActive: boolean
    amount: number
    amountUsed: number
    promotionId: number
    recurringPaymentStatus: 'active' | 'cancelled' | null
    isCancelled: boolean
    subscription?: 'monthly' | 'annual'
}

export type EmployerSubscriptionsResponse = {
    subscriptions: EmployerSubscription[]
    hasActiveSubscription: boolean
}

export type CancelSubscriptionResponse = {
    success: boolean
    message: string
    endDate: string
    expectedCapacity: number | null
    activeJobs: number | null
    expectedFrozenJobs: number | null
}

export type CancelSubscriptionPreviewResponse = {
    endDate: string
    expectedCapacity: number
    activeJobs: number
    expectedFrozenJobs: number
}

export const getEmployerSubscriptions = (
    query: { userid: number }, backdrop = false,
) => apiAction<EmployerSubscriptionsResponse>({
    url: '/api/employer/subscriptions',
    method: API_METHODS.GET,
    query,
    backdrop,
});

export const getCancelSubscriptionPreview = (
    promotionId: number,
) => apiAction<CancelSubscriptionPreviewResponse>({
    url: `/api/subscription/${promotionId}/cancellation-preview`,
    method: API_METHODS.GET,
    backdrop: false,
});

export const cancelSubscription = (
    payload: { promotionId: number }, backdrop = true,
) => apiAction<CancelSubscriptionResponse>({
    url: '/api/subscription/cancel',
    method: API_METHODS.POST,
    payload,
    backdrop,
});

export const addPromotions = (
    payload: AddPromotionPayload, backdrop = true,
) => apiAction<void>({
    url: '/api/promotions',
    method: API_METHODS.POST,
    payload,
    backdrop,
});

type UsePromotionCreditsReqBody = {
    type: PromotionTypesValues,
    amount: number,
    userid: number
    jobid: number
};

export const usePromotions = (
    payload: UsePromotionCreditsReqBody, backdrop = true,
) => apiAction<void>({
    url: '/promotions/use-credits',
    method: API_METHODS.POST,
    payload,
    backdrop,
    globalError: true,
});

export const removePromotions = (
    params: { promotionId: number }, backdrop = true,
) => apiAction<void>({
    url: `/api/promotions/${params.promotionId}`,
    method: API_METHODS.DELETE,
    backdrop,
});
